/**
 * FullStacked Cloud - Passkey registration page on the auth host (/passkeys/register).
 *
 * Clerk creates passkeys only inside a signed-in Clerk session on the page's own host, and this
 * auth host (a.fullstacked.cloud / auth.fullstacked.cloud) is where sign-in happens, so the
 * ceremony runs here. The Console opens this page with a single-use ticket in the URL fragment;
 * the page trades it for a short-lived Clerk sign-in token of the same user, signs in with it
 * (ticket strategy), runs `user.createPasskey()` and confirms with the ticket. The Console
 * session is never handed to this page.
 */

import { useEffect, useState } from "react";
import { Clerk } from "@clerk/clerk-js";
import { StatusPanel } from "./auth-panels.tsx";
import { fetchClerkConfig, postJson } from "./clerk-config.ts";
import { clerkError } from "./clerk-steps.ts";
import { openerOrigin, readTicket } from "./lib/passkey-page.ts";

type Phase =
    | { kind: "ready" }
    | { kind: "working"; message: string }
    | { kind: "done"; name: string }
    | { kind: "error"; message: string };

/** Signs the ticket's user in to Clerk on this host (replacing any other signed-in user). */
async function signInForTicket(ticket: string): Promise<Clerk> {
    const start = await postJson<{ signInToken: string; clerkUserId: string }>(
        "/v1/passkeys/register/sign-in",
        { ticket }
    );
    const cfg = await fetchClerkConfig();
    const clerk = new Clerk(cfg.publishableKey);
    await clerk.load();
    if (clerk.user?.id === start.clerkUserId) return clerk;
    if (clerk.session) await clerk.signOut();
    const signIn = await clerk.client!.signIn.create({
        strategy: "ticket",
        ticket: start.signInToken,
    });
    if (signIn.status !== "complete")
        throw new Error("Could not confirm your account for this passkey");
    await clerk.setActive({ session: signIn.createdSessionId });
    return clerk;
}

export function PasskeyRegisterView() {
    const [ticket] = useState(() => readTicket(window.location.hash));
    const [phase, setPhase] = useState<Phase>(
        ticket ? { kind: "ready" } : { kind: "error", message: "This link is missing its ticket." }
    );

    useEffect(() => {
        // Keep the ticket out of history, bookmarks and copied URLs.
        if (window.location.hash) {
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    const register = async (): Promise<void> => {
        try {
            setPhase({ kind: "working", message: "Preparing passkey..." });
            const clerk = await signInForTicket(ticket!);
            setPhase({ kind: "working", message: "Follow your device's prompt..." });
            const created = await clerk.user!.createPasskey();
            setPhase({ kind: "working", message: "Saving passkey..." });
            const res = await postJson<{ passkey: { id: string; name: string | null } }>(
                "/v1/passkeys/register",
                { ticket, passkeyId: created.id }
            );
            const name = res.passkey.name || "Passkey";
            setPhase({ kind: "done", name });
            const target = openerOrigin(document.referrer);
            const opener = window.opener as Window | null;
            if (opener && target) {
                opener.postMessage({ type: "fsc-passkey-registered", id: res.passkey.id }, target);
                window.setTimeout(() => window.close(), 1200);
            }
        } catch (err) {
            setPhase({ kind: "error", message: clerkError(err) });
        }
    };

    return (
        <div className="card">
            <div className="card-title">
                <span>Add a passkey</span>
            </div>
            <div className="stack">
                <p className="dim">
                    The passkey is created for{" "}
                    <span className="mono">{window.location.hostname}</span> and signs you in to
                    FullStacked Cloud on this environment only.
                </p>
                {phase.kind === "ready" ? (
                    <button className="btn btn-primary" onClick={() => void register()}>
                        Create passkey
                    </button>
                ) : null}
                {phase.kind === "working" ? <StatusPanel message={phase.message} /> : null}
                {phase.kind === "done" ? (
                    <StatusPanel message={`${phase.name} added. You can close this window.`} />
                ) : null}
                {phase.kind === "error" ? (
                    <StatusPanel
                        error
                        message={`${phase.message.replace(/\.?\s*$/, ".")} Start again from Console settings.`}
                    />
                ) : null}
            </div>
        </div>
    );
}
